import { useState, useEffect } from 'react';
import { useCallPanel } from '../hooks/useCallPanel';
import { usePatients } from '../hooks/usePatients';
import { useProfessionals } from '../hooks/useProfessionals';
import { CallPanel as CallPanelType } from '../types';
import { 
  Monitor, 
  Volume2, 
  Play, 
  Pause, 
  RotateCcw,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  Plus,
  Mic,
  MicOff,
  Tv
} from 'lucide-react';

export default function CallPanel() {
  const { calls, loading, addCall, updateCallStatus, deleteCall } = useCallPanel();
  const { patients } = usePatients();
  const { professionals } = useProfessionals();
  const [currentCall, setCurrentCall] = useState<CallPanelType | null>(null);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [volume, setVolume] = useState(50);
  const [isCalling, setIsCalling] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCall, setNewCall] = useState({
    patientId: '',
    professionalId: '',
    service: '',
    room: '',
    priority: 'normal' as CallPanelType['priority']
  });

  // Simular chamadas automáticas
  useEffect(() => {
    if (isAutoPlay && calls.length > 0) {
      const nextCall = calls.find(call => call.status === 'chamando');
      if (nextCall && !currentCall) {
        setCurrentCall(nextCall);
        setIsCalling(true);
        
        // Auto avançar para próximo status após 10 segundos
        setTimeout(() => {
          updateCallStatus(nextCall.id, 'atendendo');
          setTimeout(() => {
            updateCallStatus(nextCall.id, 'concluido');
            setCurrentCall(null);
            setIsCalling(false);
          }, 30000); // 30 segundos atendendo
        }, 10000); // 10 segundos chamando
      }
    }
  }, [isAutoPlay, calls, currentCall]);

  const handleCallPatient = async (call: CallPanelType) => {
    setCurrentCall(call);
    setIsCalling(true);
    await updateCallStatus(call.id, 'chamando');
  };

  const handleNextStatus = async (call: CallPanelType) => {
    switch (call.status) {
      case 'chamando':
        await updateCallStatus(call.id, 'atendendo');
        setIsCalling(false);
        break;
      case 'atendendo':
        await updateCallStatus(call.id, 'concluido');
        setCurrentCall(null);
        setIsCalling(false);
        break;
    }
  };

  const handleAddCall = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const patient = patients.find(p => p.id === newCall.patientId);
    const professional = professionals.find(p => p.id === newCall.professionalId);
    
    if (!patient || !professional) {
      alert('Selecione um paciente e um profissional válidos');
      return;
    }

    // Gerar número da senha
    const ticketNumber = String(calls.length + 1).padStart(3, '0');

    const callData: Omit<CallPanelType, 'id'> = {
      patientName: patient.name,
      ticketNumber,
      service: newCall.service,
      professional: professional.name,
      room: newCall.room,
      status: 'chamando',
      calledAt: new Date().toISOString(),
      priority: newCall.priority
    };

    await addCall(callData);
    
    // Reset form
    setNewCall({
      patientId: '',
      professionalId: '',
      service: '',
      room: '',
      priority: 'normal'
    });
    setShowAddForm(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgente':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'preferencial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'chamando':
        return 'bg-orange-100 text-orange-800';
      case 'atendendo':
        return 'bg-green-100 text-green-800';
      case 'concluido':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusName = (status: string) => {
    const statusNames = {
      'chamando': 'Chamando',
      'atendendo': 'Atendendo',
      'concluido': 'Concluído'
    };
    return statusNames[status as keyof typeof statusNames] || status;
  };

  const activeCalls = calls.filter(call => call.status !== 'concluido');
  const completedCalls = calls.filter(call => call.status === 'concluido');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Painel de Chamadas</h1>
          <p className="text-gray-600">Gerencie as chamadas de pacientes</p>
        </div>
        <div className="flex items-center space-x-4">
          <a
            href="/call-display"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
          >
            <Tv className="h-4 w-4" />
            <span>Abrir Display TV</span>
          </a>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-medical-600 text-white px-4 py-2 rounded-lg hover:bg-medical-700 flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Nova Chamada</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Volume2 className="h-5 w-5 text-gray-600" />
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => setVolume(parseInt(e.target.value))}
                className="w-20"
              />
              <span className="text-sm text-gray-600">{volume}%</span>
            </div>
            <button
              onClick={() => setIsAutoPlay(!isAutoPlay)}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                isAutoPlay 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {isAutoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span>{isAutoPlay ? 'Pausar' : 'Auto Play'}</span>
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            {isCalling ? <Mic className="h-5 w-5 text-red-600" /> : <MicOff className="h-5 w-5 text-gray-400" />}
            <span className="text-sm text-gray-600">
              {isCalling ? 'Chamando...' : 'Aguardando'}
            </span>
          </div>
        </div>
      </div>

      {/* Current Call Display */}
      {currentCall && (
        <div className={`rounded-lg p-8 text-center transition-all duration-500 ${
          isCalling 
            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white animate-pulse' 
            : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
        }`}>
          <div className="flex flex-col items-center space-y-4">
            <Monitor className="h-16 w-16" />
            <h2 className="text-3xl font-bold">
              {isCalling ? 'CHAMANDO' : 'ATENDENDO'}
            </h2>
            <div className="text-center">
              <p className="text-2xl font-semibold mb-2">{currentCall.patientName}</p>
              <p className="text-xl mb-1">Senha: {currentCall.ticketNumber}</p>
              <p className="text-lg opacity-90">{currentCall.service}</p>
              <p className="text-lg opacity-90">{currentCall.room}</p>
              <p className="text-lg opacity-90">Dr(a). {currentCall.professional}</p>
            </div>
            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => handleNextStatus(currentCall)}
                className="bg-white text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                {currentCall.status === 'chamando' ? 'Confirmar Atendimento' : 'Concluir Atendimento'}
              </button>
              <button
                onClick={() => handleCallPatient(currentCall)}
                className="bg-white bg-opacity-20 text-white px-6 py-2 rounded-lg hover:bg-opacity-30 transition-colors font-medium"
              >
                Chamar Novamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-blue-500 p-3 rounded-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Na fila</p>
              <p className="text-2xl font-semibold text-gray-900">{activeCalls.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-orange-500 p-3 rounded-lg">
              <Volume2 className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Chamando</p>
              <p className="text-2xl font-semibold text-gray-900">
                {calls.filter(call => call.status === 'chamando').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-green-500 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Atendendo</p>
              <p className="text-2xl font-semibold text-gray-900">
                {calls.filter(call => call.status === 'atendendo').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="bg-gray-500 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Concluídos</p>
              <p className="text-2xl font-semibold text-gray-900">{completedCalls.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Calls Queue */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Fila de Atendimento</h3>
        </div>
        
        <div className="p-6">
          {activeCalls.length > 0 ? (
            <div className="space-y-4">
              {activeCalls.map((call) => (
                <div key={call.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(call.priority)}`}>
                      {call.priority === 'urgente' && <AlertTriangle className="inline h-4 w-4 mr-1" />}
                      {call.ticketNumber}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{call.patientName}</h4>
                      <p className="text-sm text-gray-600">{call.service} - {call.room}</p>
                      <p className="text-sm text-gray-600">Dr(a). {call.professional}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(call.status)}`}>
                      {getStatusName(call.status)}
                    </span>
                    <div className="text-sm text-gray-500">
                      {new Date(call.calledAt).toLocaleTimeString('pt-BR')}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleCallPatient(call)}
                        className="bg-medical-600 text-white px-3 py-1 rounded text-sm hover:bg-medical-700 transition-colors"
                      >
                        Chamar
                      </button>
                      {call.status === 'chamando' && (
                        <button
                          onClick={() => handleNextStatus(call)}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                        >
                          Atender
                        </button>
                      )}
                      {call.status === 'atendendo' && (
                        <button
                          onClick={() => handleNextStatus(call)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Concluir
                        </button>
                      )}
                      <button
                        onClick={() => deleteCall(call.id)}
                        className="text-red-600 hover:text-red-800 px-2 py-1"
                        title="Remover da fila"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Monitor className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Nenhuma chamada na fila</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Call Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nova Chamada</h2>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddCall} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paciente *
                </label>
                <select
                  value={newCall.patientId}
                  onChange={(e) => setNewCall(prev => ({ ...prev, patientId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                >
                  <option value="">Selecione um paciente</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profissional *
                </label>
                <select
                  value={newCall.professionalId}
                  onChange={(e) => setNewCall(prev => ({ ...prev, professionalId: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                >
                  <option value="">Selecione um profissional</option>
                  {professionals.map(professional => (
                    <option key={professional.id} value={professional.id}>
                      {professional.name} - {professional.position}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serviço *
                </label>
                <input
                  type="text"
                  value={newCall.service}
                  onChange={(e) => setNewCall(prev => ({ ...prev, service: e.target.value }))}
                  required
                  placeholder="Ex: Clínica Médica, Psicologia..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sala/Consultório *
                </label>
                <input
                  type="text"
                  value={newCall.room}
                  onChange={(e) => setNewCall(prev => ({ ...prev, room: e.target.value }))}
                  required
                  placeholder="Ex: Consultório 1, Sala 201..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prioridade
                </label>
                <select
                  value={newCall.priority}
                  onChange={(e) => setNewCall(prev => ({ ...prev, priority: e.target.value as CallPanelType['priority'] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
                >
                  <option value="normal">Normal</option>
                  <option value="preferencial">Preferencial</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-medical-600 text-white rounded-lg hover:bg-medical-700 transition-colors"
                >
                  Adicionar Chamada
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}